<template>
    <v-dialog
        v-model="visible"
        :max-width="900"
        persistent
        no-click-animation
        scrollable
        overlay-color="#000"
        overlay-opacity="0.85"
        :fullscreen="phoneOnly"
        @keydown.esc.stop.prevent
    >
        <v-card class="policy-card" :tile="phoneOnly">
            <v-card-title class="policy-card-title">
                <v-icon color="indigo" class="me-2">mdi-school-outline</v-icon>
                Student Classes Policy
            </v-card-title>
            <v-divider />
            <v-card-text class="policy-card-text">
                <div class="must-acknowledge mb-3">
                    <v-icon size="18" color="orange darken-2" class="me-1">mdi-information-outline</v-icon>
                    Please review and accept the policy below to continue using your account.
                </div>
                <div class="policy-html" v-html="policyHtml"></div>
            </v-card-text>
            <v-divider />
            <v-card-actions class="policy-card-actions">
                <v-spacer />
                <v-btn
                    color="primary"
                    depressed
                    large
                    :loading="acknowledging"
                    :disabled="acknowledging"
                    @click="acknowledge"
                >
                    <v-icon left>mdi-check-circle</v-icon>
                    I Agree
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'

export default {
    name: 'student-policy-dialog',
    data(){
        return {
            acknowledging: false,
        }
    },
    computed: {
        ...GlobalComputed,
        policyHtml(){
            return this.appData ? (this.appData.student_classes_policy || '') : '';
        },
        publishedAt(){
            return this.appData ? this.appData.student_classes_policy_published_at : null;
        },
        acknowledgedAt(){
            return this.user ? this.user.student_policy_acknowledged_at : null;
        },
        needsAcknowledgement(){
            if (!this.isAuthed || !this.isStudent) return false;
            if (!this.publishedAt) return false;
            const policyStripped = this.policyHtml ? String(this.policyHtml).replace(/<[^>]*>/g, '').trim() : '';
            if (!policyStripped) return false;
            // Gate onboarding: don't show the policy until the user has
            // verified their email, picked their interests, AND booked their
            // initial session (whichever flow they signed up under). The
            // booking-redirect flags only flip true AFTER interests are saved,
            // so we need an explicit interests_completed check too — otherwise
            // the dialog would show on top of the interests page.
            if (!this.user.email_verified_at) return false;
            if (!this.user.has_active_enrollment) return false;
            if (this.user.needs_evaluation_redirect) return false;
            if (this.user.needs_consultation_redirect) return false;
            if (!this.acknowledgedAt) return true;
            // Both timestamps are ISO strings; lexicographic compare works for ISO 8601
            return new Date(this.acknowledgedAt).getTime() < new Date(this.publishedAt).getTime();
        },
        visible: {
            get(){ return this.needsAcknowledgement; },
            set(){ /* persistent — cannot be dismissed externally */ },
        },
    },
    methods: {
        ...GlobalMethods,
        acknowledge(){
            if (this.acknowledging) return;
            this.acknowledging = true;
            window.axios.post('/policy/student-classes/acknowledge').then(() => {
                this.$store.dispatch('refreshUser').then(() => {
                    this.acknowledging = false;
                }).catch(() => {
                    this.acknowledging = false;
                });
            }).catch(() => {
                this.acknowledging = false;
                this.showMsg('Could not record your acknowledgement, please try again', 'error');
            });
        },
    },
}
</script>

<style lang="scss" scoped>
.policy-card {
    border-radius: 12px;
    overflow: hidden;
}
.policy-card-title {
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 18px;
    color: #2A2A2A;
    padding: 18px 22px;
}
.policy-card-text {
    padding: 20px 24px !important;
    max-height: 70vh;
    background: #FFFFFF;
}
.policy-card-actions {
    padding: 14px 18px;
    background: #FAFAFA;
}
.must-acknowledge {
    background: #FFF7E6;
    border: 1px solid #FFE0A8;
    color: #8A5300;
    border-radius: 6px;
    padding: 10px 12px;
    font-size: 13px;
    line-height: 1.5;
    display: flex;
    align-items: flex-start;
}
</style>

<style lang="scss">
/* Render output produced by Quill (vue2-editor) — declared unscoped so styles
   reach the v-html content rendered by Vuetify's detached dialog overlay. */
.policy-html {
    font-family: 'Inter', sans-serif;
    font-size: 14.5px;
    line-height: 1.75;
    color: #3D3D3D;
    word-break: break-word;

    h1, h2, h3, h4 {
        font-weight: 600;
        color: #2A2A2A;
        margin: 1.2em 0 0.5em;
        line-height: 1.3;
    }
    h1 { font-size: 1.8em; }
    h2 { font-size: 1.5em; }
    h3 { font-size: 1.25em; }
    h4 { font-size: 1.1em; }

    p { margin: 0 0 0.85em; }
    p:last-child { margin-bottom: 0; }

    ul, ol { padding-left: 1.5em; margin: 0 0 0.85em; }
    li { margin: 0.2em 0; }

    strong, b { font-weight: 600; }
    em, i { font-style: italic; }
    u { text-decoration: underline; }
    s { text-decoration: line-through; }

    a { color: #1976D2; text-decoration: underline; }

    blockquote {
        border-left: 3px solid #E0E0E0;
        padding: 4px 14px;
        margin: 0.5em 0;
        color: #555;
        background: #FAFAFA;
        border-radius: 0 6px 6px 0;
    }

    pre, code {
        font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
        font-size: 0.92em;
    }
    pre {
        background: #F5F5F5;
        padding: 10px 14px;
        border-radius: 6px;
        overflow-x: auto;
        white-space: pre-wrap;
    }
    code { background: #F0F0F0; padding: 1px 5px; border-radius: 3px; }

    img { max-width: 100%; height: auto; border-radius: 6px; }

    .ql-align-center { text-align: center; }
    .ql-align-right  { text-align: right; }
    .ql-align-justify { text-align: justify; }

    .ql-indent-1 { padding-left: 3em; }
    .ql-indent-2 { padding-left: 6em; }
    .ql-indent-3 { padding-left: 9em; }
    .ql-indent-4 { padding-left: 12em; }
}
</style>
